import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { globalSearch, type GlobalSearchResponse, type SearchResultItem } from './api';

const groupLabels: Record<keyof GlobalSearchResponse['groups'], string> = {
  companies: 'Aziende',
  projects: 'Progetti',
  tasks: 'Attività',
  publications: 'Contenuti pubblicati',
};

export function GlobalSearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const normalizedQuery = query.trim();
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(normalizedQuery), 250);
    return () => window.clearTimeout(timer);
  }, [normalizedQuery]);
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);
  const results = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: () => globalSearch(debouncedQuery),
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
  const currentData =
    debouncedQuery === normalizedQuery && results.data?.query === normalizedQuery
      ? results.data
      : undefined;
  const groups = useMemo(
    () =>
      currentData
        ? (
            Object.entries(currentData.groups) as Array<
              [keyof GlobalSearchResponse['groups'], SearchResultItem[]]
            >
          ).filter(([, items]) => items.length > 0)
        : [],
    [currentData],
  );
  const isSearching =
    normalizedQuery.length >= 2 &&
    !currentData &&
    (debouncedQuery !== normalizedQuery || results.isPending || results.isFetching);
  const isCurrentError =
    debouncedQuery === normalizedQuery && normalizedQuery.length >= 2 && results.isError;
  const openResult = (href: string) => {
    onClose();
    void navigate(href);
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="span" sx={{ fontWeight: 850 }} variant="h6">
            Cerca nella piattaforma
          </Typography>
          <IconButton aria-label="Chiudi ricerca" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <TextField
            autoFocus
            fullWidth
            label="Cerca"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Azienda, progetto, attività o contenuto"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              },
            }}
            value={query}
          />
          {normalizedQuery.length < 2 && (
            <Typography color="text.secondary" variant="body2">
              Inserisci almeno due caratteri. I risultati rispettano il tuo ruolo e gli accessi
              assegnati.
            </Typography>
          )}
          {isSearching && (
            <Box sx={{ display: 'grid', minHeight: 120, placeItems: 'center' }}>
              <CircularProgress aria-label="Ricerca in corso" />
            </Box>
          )}
          {isCurrentError && (
            <Alert severity="error">Ricerca temporaneamente non disponibile.</Alert>
          )}
          {currentData && groups.length === 0 && (
            <Alert severity="info">Nessun risultato accessibile trovato.</Alert>
          )}
          {groups.map(([group, items], groupIndex) => (
            <Box key={group}>
              {groupIndex > 0 && <Divider sx={{ mb: 2 }} />}
              <Typography color="secondary" sx={{ fontWeight: 900 }} variant="overline">
                {groupLabels[group]}
              </Typography>
              <List aria-label={`Risultati ${groupLabels[group]}`} disablePadding>
                {items.map((item) => (
                  <ListItem disablePadding key={item.id}>
                    <ListItemButton onClick={() => openResult(item.href)}>
                      <ListItemText primary={item.title} secondary={item.context} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
